import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import { cn } from '../lib/utils.js';
import { Label } from './label.js';

export const FieldGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('grid gap-6', className)} {...props} />
  ),
);
FieldGroup.displayName = 'FieldGroup';

export const Field = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('grid gap-2', className)} {...props} />
  ),
);
Field.displayName = 'Field';

export const FieldLabel = React.forwardRef<
  React.ElementRef<typeof Label>,
  React.ComponentPropsWithoutRef<typeof Label>
>(({ className, ...props }, ref) => (
  <Label ref={ref} className={cn('text-sm font-medium', className)} {...props} />
));
FieldLabel.displayName = 'FieldLabel';

export const FieldDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'text-sm text-muted-foreground [&_a]:underline [&_a]:underline-offset-4',
      className,
    )}
    {...props}
  />
));
FieldDescription.displayName = 'FieldDescription';

export function FieldSeparator({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('relative flex items-center text-xs text-muted-foreground', className)}
      {...props}
    >
      <div className="h-px flex-1 bg-border" />
      {children ? <span className="px-3">{children}</span> : null}
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

export const FieldControl = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement> & { asChild?: boolean }
>(({ asChild = true, className, ...props }, ref) => {
  if (asChild) {
    return <Slot ref={ref} className={className} {...props} />;
  }

  return <div ref={ref as React.Ref<HTMLDivElement>} className={className} {...props} />;
});
FieldControl.displayName = 'FieldControl';
